import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const Counter = ({ to, suffix = "", duration = 2200 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {val}
      {suffix}
    </span>
  );
};

const items = [
  { value: 35, suffix: "%", label: "Avg FPS Increase", note: "Across 1,200+ rigs" },
  { value: 150, suffix: "%", label: "Better 1% Lows", note: "Goodbye stutter" },
  { value: 27, suffix: "+", label: "Verified Positive Reviews", note: "Real customers" },
  { value: 100, suffix: "%", label: "Satisfaction Rate", note: "27+ Verified Reviews" },
];

export const Stats = () => {
  return (
    <section
      id="stats"
      className="relative py-28 sm:py-36 border-y border-white/5 overflow-hidden"
      data-testid="stats-section"
    >
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.6 }}
          className="mb-14 sm:mb-20 max-w-2xl"
        >
          <p className="text-orange-500 font-bold tracking-[0.3em] text-xs uppercase mb-4 slash">
            The Numbers Don't Lie
          </p>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95]"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Every frame you don't have <span className="text-zinc-600">is</span>{" "}
            <span className="text-orange-500">a kill you missed.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <motion.div
              key={it.label}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="glass glass-hover !rounded-2xl p-8 sm:p-10 min-h-[280px] sm:min-h-[320px] flex flex-col items-center justify-center text-center group"
              data-testid={`stat-card-${i}`}
            >
              <span
                className="text-6xl sm:text-7xl lg:text-8xl font-black text-white leading-none tracking-tighter group-hover:text-orange-500 transition-colors tabular-nums"
                style={{ fontFamily: "'Orbitron', sans-serif" }}
                data-testid={`stat-value-${i}`}
              >
                <Counter to={it.value} suffix={it.suffix} />
              </span>
              <div className="mt-6 h-px w-12 bg-orange-500" />
              <div className="mt-4 text-white font-bold uppercase tracking-widest text-sm">
                {it.label}
              </div>
              <div className="mt-2 text-zinc-500 text-sm">{it.note}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

